/**
 * MicBooster — amplifies the rep's outgoing microphone so the customer hears
 * them louder. The browser's autoGainControl normalizes to a fixed (often
 * conservative) level, which leaves quiet mics / reps sitting back from the mic
 * too soft on the far end. This inserts a real WebAudio gain stage into the
 * outbound track and swaps it onto the peer connection via replaceTrack.
 *
 * Gain is kept modest by default (1.5x) — pushing too hard clips and distorts,
 * which sounds WORSE than quiet. Tune via the constructor if needed.
 */
export class MicBooster {
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private gainNode: GainNode | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;
  private origTrack: MediaStreamTrack | null = null;
  private sender: RTCRtpSender | null = null;

  constructor(private gain = 1.5) {}

  /**
   * Route the audio sender's mic track through a gain stage. Best-effort:
   * returns the boosted track (for the recorder to mix) or null if anything
   * fails — a failure must never break the call, just skip the boost.
   */
  async apply(pc: RTCPeerConnection): Promise<MediaStreamTrack | null> {
    try {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
      if (!sender || !sender.track) return null;
      this.sender = sender;
      this.origTrack = sender.track;

      const AC = window.AudioContext || (window as any).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();

      this.source = this.ctx.createMediaStreamSource(new MediaStream([this.origTrack]));
      this.gainNode = this.ctx.createGain();
      this.gainNode.gain.value = this.gain;
      this.dest = this.ctx.createMediaStreamDestination();
      this.source.connect(this.gainNode).connect(this.dest);

      const boosted = this.dest.stream.getAudioTracks()[0];
      if (!boosted) return null;

      // Swap the boosted track in without renegotiation.
      await sender.replaceTrack(boosted);
      return boosted;
    } catch {
      // Roll back to the original track if the swap half-happened.
      try {
        if (this.sender && this.origTrack) await this.sender.replaceTrack(this.origTrack);
      } catch { /* noop */ }
      this.dispose();
      return null;
    }
  }

  /** Change the boost live (e.g. from a future UI control). 1.0 = unity. */
  setGain(g: number) {
    if (this.gainNode) this.gainNode.gain.value = g;
    this.gain = g;
  }

  /** Restore the original mic track and tear down the audio graph. */
  async dispose() {
    try {
      if (this.sender && this.origTrack) await this.sender.replaceTrack(this.origTrack);
    } catch { /* noop */ }
    try { this.source?.disconnect(); } catch { /* noop */ }
    try { this.gainNode?.disconnect(); } catch { /* noop */ }
    try { await this.ctx?.close(); } catch { /* noop */ }
    this.ctx = null; this.source = null; this.gainNode = null;
    this.dest = null; this.origTrack = null; this.sender = null;
  }
}
